import {
  FederalTaxClassification,
  LlcClassification,
  SubmitW8BenTaxDocumentPayload,
  SubmitW9TaxDocumentPayload,
  TaxDocument,
  TaxDocumentAddress,
  TaxDocumentFormType,
  TaxIdType
} from 'shared/types/payment-account.interface';
import { IIdValue } from 'shared/types/id-value.interface';

export interface W9PayloadFormValue {
  fullLegalName: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  taxIdType: TaxIdType;
  taxIdNumber: string;
  federalTaxClassification: FederalTaxClassification;
  llcClassification: string | null | undefined;
  iCertify: boolean;
  signerName: string;
  signedAt: string;
}

export interface W8BenPayloadFormValue {
  fullName: string;
  countryOfCitizenship: string;
  permanentResidenceAddress: string;
  permanentCity: string;
  permanentCountry: string;
  sameMailingAddress: boolean;
  mailingAddress: string | null | undefined;
  mailingCity: string | null | undefined;
  mailingCountry: string | null | undefined;
  foreignTaxIdNumber: string;
  iCertify: boolean;
  signerName: string;
  signedAt: string;
}

export interface TaxDocumentationReadonlyRow {
  id: string;
  label: string;
  value: string;
}

export const TAX_DOCUMENT_FEDERAL_CLASSIFICATIONS: IIdValue[] = [
  { id: 'individual', localizable_name: 'profile.individualSoleProprietor', value: 'individual' },
  { id: 'c_corp', localizable_name: 'profile.cCorporation', value: 'c_corp' },
  { id: 's_corp', localizable_name: 'profile.sCorporation', value: 's_corp' },
  { id: 'partnership', localizable_name: 'profile.partnership', value: 'partnership' },
  { id: 'trust_estate', localizable_name: 'profile.trustEstate', value: 'trust_estate' },
  { id: 'llc', localizable_name: 'profile.llc', value: 'llc' },
  { id: 'other', localizable_name: 'profile.other', value: 'other' }
];

export const TAX_DOCUMENT_LLC_CLASSIFICATIONS: IIdValue[] = [
  { id: 'c', localizable_name: 'profile.llcClassificationC', value: 'c' },
  { id: 's', localizable_name: 'profile.llcClassificationS', value: 's' },
  { id: 'p', localizable_name: 'profile.llcClassificationP', value: 'p' }
];

export const TAX_DOCUMENT_ORIGINAL_FORM_URLS: Record<TaxDocumentFormType, string> = {
  W9: 'https://www.irs.gov/pub/irs-pdf/fw9.pdf',
  W8BEN: 'https://www.irs.gov/pub/irs-pdf/fw8ben.pdf'
};

export function getTaxDocumentFormNameKey(formType: TaxDocumentFormType): string {
  return formType === 'W8BEN' ? 'profile.w8BenFormName' : 'profile.w9FormName';
}

export function getTaxDocumentOriginalFormUrl(formType: TaxDocumentFormType): string {
  return TAX_DOCUMENT_ORIGINAL_FORM_URLS[formType];
}

export function getTaxDocumentResidencySuffixKey(formType: TaxDocumentFormType): string {
  return formType === 'W8BEN' ? 'profile.forNonUsResidents' : 'profile.forUsResidents';
}

export function buildW9Payload(value: W9PayloadFormValue): SubmitW9TaxDocumentPayload {
  const llcClassification = normalizeLlcClassification(value.llcClassification);

  return {
    form_type: 'W9',
    full_legal_name: trim(value.fullLegalName),
    address: {
      street_address: trim(value.streetAddress),
      city: trim(value.city),
      state: trim(value.state),
      zip_code: trim(value.zipCode),
      country: trim(value.country)
    },
    tax_id: {
      type: value.taxIdType,
      value: formatTaxId(value.taxIdNumber, value.taxIdType)
    },
    federal_tax_classification: value.federalTaxClassification,
    llc_classification: value.federalTaxClassification === 'llc' ? llcClassification : null,
    certification: {
      electronic_signature: !!value.iCertify,
      signer_name: trim(value.signerName),
      signed_at: value.signedAt
    }
  };
}

export function buildW8BenPayload(value: W8BenPayloadFormValue): SubmitW8BenTaxDocumentPayload {
  const sameMailingAddress = !!value.sameMailingAddress;

  return {
    form_type: 'W8BEN',
    full_legal_name: trim(value.fullName),
    country_of_citizenship: trim(value.countryOfCitizenship),
    permanent_residence_address: {
      street_address: trim(value.permanentResidenceAddress),
      city: trim(value.permanentCity),
      country: trim(value.permanentCountry)
    },
    mailing_address_same_as_permanent: sameMailingAddress,
    mailing_address: sameMailingAddress
      ? {
          same_as_permanent: true,
          street_address: null,
          city: null,
          country: null
        }
      : {
          same_as_permanent: false,
          street_address: trim(value.mailingAddress),
          city: trim(value.mailingCity),
          country: trim(value.mailingCountry)
        },
    foreign_tax_id_number: trim(value.foreignTaxIdNumber),
    certification: {
      electronic_signature: !!value.iCertify,
      signer_name: trim(value.signerName),
      signed_at: value.signedAt
    }
  };
}

export function buildTaxDocumentReadonlyRows(
  taxDocument: TaxDocument,
  translate: (key: string) => string
): TaxDocumentationReadonlyRow[] {
  const commonRows: TaxDocumentationReadonlyRow[] = [
    {
      id: 'fullLegalName',
      label: translate('profile.fullLegalName'),
      value: taxDocument.full_legal_name
    }
  ];

  if (taxDocument.form_type === 'W9') {
    return filterReadonlyRows([
      ...commonRows,
      {
        id: 'address',
        label: translate('profile.taxDocumentationStreetAddress'),
        value: formatAddress(taxDocument.address)
      },
      {
        id: 'taxId',
        label: translate(
          taxDocument.tax_id.type === 'ein' ? 'profile.employerIdentificationNumber' : 'profile.socialSecurutyNumber'
        ),
        value: taxDocument.tax_id.masked
      },
      {
        id: 'usTaxStatus',
        label: translate('profile.usTaxStatus'),
        value: translate('profile.USCitizen')
      }
    ]);
  }

  return filterReadonlyRows([
    ...commonRows,
    {
      id: 'countryOfCitizenship',
      label: translate('profile.countryOfCitizenship'),
      value: taxDocument.country_of_citizenship
    },
    {
      id: 'permanentResidenceAddress',
      label: translate('profile.permanentResidenceAddress'),
      value: formatAddress(taxDocument.permanent_residence_address)
    },
    {
      id: 'mailingAddress',
      label: translate('profile.mailingAddress'),
      value: taxDocument.mailing_address_same_as_permanent
        ? translate('profile.sameMailingAddress')
        : formatAddress(taxDocument.mailing_address)
    },
    {
      id: 'foreignTaxIdNumber',
      label: translate('profile.foreignTaxIdNumber'),
      value: taxDocument.foreign_tax_id_number
    },
    {
      id: 'certification',
      label: translate('profile.electronicSignatureCetifif'),
      value: taxDocument.certification.signer_name
    }
  ]);
}

function normalizeLlcClassification(value: string | null | undefined): LlcClassification | null {
  const normalized = trim(value).toLowerCase();

  return normalized === 'c' || normalized === 's' || normalized === 'p' ? normalized : null;
}

function formatTaxId(taxIdNumber: string | null | undefined, taxIdType: TaxIdType): string {
  const rawTaxId = (taxIdNumber || '').replace(/\D/g, '');

  if (rawTaxId.length !== 9) {
    return trim(taxIdNumber);
  }

  return taxIdType === 'ein'
    ? `${rawTaxId.slice(0, 2)}-${rawTaxId.slice(2)}`
    : `${rawTaxId.slice(0, 3)}-${rawTaxId.slice(3, 5)}-${rawTaxId.slice(5)}`;
}

function formatAddress(address: TaxDocumentAddress | null | undefined): string {
  if (!address) {
    return '';
  }

  const regionLine = [address.city, [address.state, address.zip_code].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');

  return [address.street_address, regionLine, address.country].filter(Boolean).join(', ');
}

function filterReadonlyRows(rows: TaxDocumentationReadonlyRow[]): TaxDocumentationReadonlyRow[] {
  return rows.filter(row => !!row.value);
}

function trim(value: string | null | undefined): string {
  return value?.trim() ?? '';
}
